<template>
  <div class="post_detail">
    <div v-if="detailLoading" class="post_detail__status">帖子加载中...</div>

    <div v-else-if="detailError" class="post_detail__status post_detail__status--error">
      <p>{{ detailErrorText }}</p>
      <button type="button" @click="bootstrap(true)">点击重试</button>
    </div>

    <template v-else-if="hasDetail">
      <div class="post_detail__card">
        <div v-if="isAuditing" class="post_detail__audit">审核中，仅自己可见</div>
        <div v-if="showEnterGroup" class="post_detail__top_action">
          <button type="button" @click="goGroup">进论坛</button>
        </div>

        <div class="post_detail__user">
          <div class="post_detail__avatar_wrap">
            <div class="post_detail__avatar" :style="{ backgroundImage: `url(${detail.avatar || FORUM_DEFAULT_AVATAR})` }" />
            <div v-if="frameUrl" class="post_detail__frame" :style="{ backgroundImage: `url(${frameUrl})` }" />
          </div>
          <div class="post_detail__user_info">
            <div class="post_detail__name_row">
              <div class="post_detail__name_main">
                <span class="post_detail__name text_ellipsis">{{ detail.nickname || '昵称为空' }}</span>
              </div>
              <button
                v-if="isOwnPost"
                type="button"
                class="post_detail__more_btn"
                aria-label="更多操作"
                @click="showPostMoreSheet = true"
              >···</button>
            </div>
            <p v-if="detailTime" class="post_detail__time">{{ detailTime }}</p>
          </div>
        </div>

        <p class="post_detail__title">
          <span v-if="detail.topStatus === 1" class="post_detail__tag post_detail__tag--top" :style="detail.topColor ? { background: detail.topColor } : undefined">置顶</span>
          <span v-if="detail.essence === 1" class="post_detail__tag post_detail__tag--essence">精华</span>
          <span v-for="tag in detail.postTagVos || []" :key="tag.tagName" class="post_detail__tag" :style="{ background: tag.backgroundImage || tag.colour || '#d09962' }">{{ tag.tagName }}</span>
          <span class="post_detail__title_text">{{ detail.title || '无标题' }}</span>
        </p>

        <div
          v-if="postContent"
          class="post_detail__content"
          @click="onPostContentClick"
          v-html="postContent"
        />

        <div v-if="mediaList.length" class="post_detail__img_list">
          <div v-for="(media, index) in mediaList" :key="getMediaKey(media, index)" class="post_detail__img_item">
            <button
              v-if="media.videoUrl"
              type="button"
              class="post_detail__media post_detail__video_card"
              :style="getForumMediaBackgroundStyle(media)"
              @click="openVideoPreview(media)"
            >
              <video
                v-if="!getForumMediaCoverUrl(media)"
                :src="media.videoUrl"
                muted
                playsinline
                webkit-playsinline
                preload="metadata"
                class="post_detail__video_probe"
              />
              <span class="post_detail__video_shade"></span>
              <span class="post_detail__video_play">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </span>
              <span v-if="media.duration" class="post_detail__video_duration">{{ formatMediaDuration(media.duration) }}</span>
            </button>
            <div
              v-else-if="!mediaLoadError[getMediaKey(media, index)]"
              class="post_detail__media"
              :style="getForumMediaBackgroundStyle(media)"
              @click="previewPostImage(media)"
            >
              <img class="post_detail__media_probe" :src="mediaImageUrl(media)" alt="" @error="markMediaError(media, index)" />
            </div>
            <div v-else class="post_detail__media_error">图片加载失败</div>
          </div>
        </div>

        <div class="post_detail__com_top">
          <div ref="commentTabs" class="post_detail__com_tabs">
            <button type="button" :class="['post_detail__com_tab', { active: activeTab === 'all' }]" @click="onTabChange('all')">
              全部评论
              <span class="post_detail__com_count">{{ commentNum || 0 }}</span>
            </button>
            <button v-if="showOwnerTab" type="button" :class="['post_detail__com_tab', { active: activeTab === 'owner' }]" @click="onTabChange('owner')">只看楼主</button>
          </div>
          <button v-if="showCommentSort" type="button" class="post_detail__com_sort" @click="onToggleSort">{{ sortType === 0 ? '最新评论' : '最热评论' }}</button>
        </div>

        <van-list v-model="commentLoading" :finished="commentFinished" :immediate-check="false" :offset="80" :finished-text="commentFinishedText" @load="fetchComments(false)">
          <CommentItem
            v-for="item in comments"
            :key="getCommentId(item)"
            :data="item"
            :post-id="postId"
            :owner-uid="ownerUid"
            :reply-count="getReplyCount(item)"
            @reply="onReply"
            @reply-sub="onReplySub"
            @praise="onPraiseComment"
          />
        </van-list>
        <div v-if="commentError" class="post_detail__comment_error">
          <span>加载失败</span>
          <button type="button" @click="fetchComments(true)">点击重试</button>
        </div>
        <EmptyState v-else-if="!commentLoading && comments.length === 0 && commentFinished" text="暂无评论，快来抢沙发" />
      </div>

      <div class="post_detail__bottom">
        <button type="button" class="post_detail__bottom_input" @click="openCommentBox()">
          <svg class="post_detail__bottom_input_icon" viewBox="0 0 24 24" fill="none" stroke="#999" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
          <span>我也来评论一下~</span>
        </button>
        <button type="button" class="post_detail__bottom_btn" @click="onCollectPost">
          <svg viewBox="0 0 24 24" class="post_detail__bottom_icon" :fill="detail.collect ? '#ff7352' : 'none'" :stroke="detail.collect ? '#ff7352' : '#c2c2c2'" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
          <span class="post_detail__bottom_count" :class="{ 'post_detail__bottom_count--active': detail.collect }">{{ detail.collectNum || 0 }}</span>
        </button>
        <button type="button" class="post_detail__bottom_btn" @click="onPraisePost">
          <svg viewBox="0 0 24 24" class="post_detail__bottom_icon" :fill="detail.praise ? '#ff7352' : 'none'" :stroke="detail.praise ? '#ff7352' : '#c2c2c2'" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
          </svg>
          <span class="post_detail__bottom_count" :class="{ 'post_detail__bottom_count--active': detail.praise }">{{ detail.praiseNum || 0 }}</span>
        </button>
      </div>
    </template>

    <van-popup v-model="showCommentBox" position="bottom">
      <div class="comment_box">
        <textarea ref="commentInput" v-model="commentContent" maxlength="300" :placeholder="replyTarget && replyTarget.nickname ? `回复 ${replyTarget.nickname}：` : '最多支持输入300字'" />
        <div v-if="commentImg.preview || commentImg.url" class="comment_box__preview">
          <img :src="commentImg.preview || commentImg.url" alt="" />
          <button type="button" @click="removeCommentImage">×</button>
          <span v-if="commentImg.uploading">上传中...</span>
          <span v-else-if="commentImg.failed">上传失败</span>
        </div>
        <div class="comment_box__op">
          <button type="button" class="comment_box__upload" @click="pickCommentImage">图片</button>
          <button type="button" class="comment_box__send" :disabled="commentSubmitting" @click="submitComment">{{ commentSubmitting ? '发送中...' : '发送' }}</button>
        </div>
        <input ref="commentImageInput" type="file" accept="image/*" class="comment_box__file" @change="onCommentImagePicked" />
      </div>
    </van-popup>

    <div v-if="previewVideo" class="video_preview">
      <button type="button" class="video_preview__close" @click="closeVideoPreview">×</button>
      <video
        ref="previewVideoPlayer"
        :src="previewVideo.videoUrl"
        :poster="getForumMediaCoverUrl(previewVideo)"
        controls
        autoplay
        playsinline
        webkit-playsinline
        class="video_preview__player"
        @error="onVideoPreviewError"
      />
      <div class="video_preview__hint">点击右上角关闭</div>
    </div>

    <van-action-sheet
      v-model="showPostMoreSheet"
      :actions="postMoreActions"
      cancel-text="取消"
      close-on-click-action
      @select="onPostMoreSelect"
    />
  </div>
</template>

<script>
import { ActionSheet, Dialog, ImagePreview, List, Popup, Toast } from 'vant';
import {
  collectOrCancelPosts,
  comment,
  deleteOwnPost,
  getCommentList,
  getPostDetail,
  getUserCenter,
  praiseOrCancelComments,
  praiseOrCancelPosts,
  uploadForumFile,
} from '@/api/forum';
import { getForumMediaBackgroundStyle, getForumMediaCoverUrl } from '@/utils/forum/media';
import { isInteractionOk, toastInteraction } from '@/utils/forum/forumInteraction';
import { markForumPostDeleted } from '@/utils/forum/groupReturnAction';
import { saveForumPostInteractionFromPost } from '@/utils/forum/postInteractionCache';
import { readForumPostSnapshot } from '@/utils/forum/postSnapshotCache';
import { resolveForumContentLink } from '@/utils/forum/contentLink';
import CommentItem from './components/CommentItem.vue';
import EmptyState from './components/EmptyState.vue';
import { FORUM_DEFAULT_AVATAR } from './constants';
import trackingMixin, { EVENT_TYPE_ENUM } from './mixins/tracking';
import forumLoginRefreshMixin from './mixins/forumLoginRefresh';

const PAGE_SIZE = 20;
const pendingCommentPraiseIds = new Set();

export default {
  name: 'ForumPostDetail',
  mixins: [trackingMixin, forumLoginRefreshMixin],
  components: { [List.name]: List, [Popup.name]: Popup, [ActionSheet.name]: ActionSheet, CommentItem, EmptyState },
  data() {
    return {
      FORUM_DEFAULT_AVATAR,
      detail: {},
      comments: [],
      commentNum: 0,
      page: 1,
      detailLoading: false,
      detailError: false,
      detailErrorText: '帖子加载失败，请稍后重试',
      commentLoading: false,
      commentRequesting: false,
      commentFinished: false,
      commentError: false,
      showOwnerTab: false,
      showCommentSort: false,
      activeTab: 'all',
      sortType: 0,
      showCommentBox: false,
      commentContent: '',
      commentImg: {},
      commentSubmitting: false,
      replyTarget: null,
      previewVideo: null,
      mediaLoadError: {},
      bootstrappedPostId: '',
      detailRequestSeq: 0,
      currentUid: '',
      showPostMoreSheet: false,
    };
  },
  computed: {
    postMoreActions() {
      return [{ name: '删除', color: '#ee0a24', key: 'delete' }];
    },
    postId() {
      return String(this.$route.params.postId || '');
    },
    /** 帖子正文 HTML，由模板 v-html 渲染 */
    postContent() {
      return (this.detail.content || '').trim();
    },
    hasDetail() {
      return Boolean(this.detail.postsId || this.detail.postId);
    },
    detailTime() {
      return this.detail.timeDesc || this.detail.createTime || this.detail.createdAt || '';
    },
    frameUrl() {
      return this.detail.frameUrl || (this.detail.userLevelFrameInfoVo && this.detail.userLevelFrameInfoVo.frameUrl) || '';
    },
    ownerUid() {
      return this.detail.uid || this.detail.userId || this.detail.authorUid || this.detail.publisherUid;
    },
    isOwnPost() {
      const postUid = this.ownerUid;
      return postUid && this.currentUid && String(postUid) === String(this.currentUid);
    },
    isAuditing() {
      return this.detail.auditStatus === 0 || this.detail.status === 'auditing' || this.detail.auditStatus === 'auditing';
    },
    showEnterGroup() {
      return Boolean(this.detail.groupId && this.$route.query.from !== 'group');
    },
    mediaList() {
      return this.detail.postsDetailVos || this.detail.detailList || [];
    },
    postImageUrls() {
      return this.mediaList.filter((item) => !item.videoUrl).map(this.mediaImageUrl).filter(Boolean);
    },
    commentFinishedText() {
      return Array.isArray(this.comments) && this.comments.length > 0 ? '没有更多了' : '';
    },
  },
  watch: {
    postId(newPostId, oldPostId) {
      if (newPostId && newPostId !== oldPostId) {
        this.bootstrap(true);
      }
    },
  },
  activated() {
    this.bootstrap(true);
  },
  deactivated() {
    this.closeVideoPreview();
    this.closeCommentBox();
  },
  beforeDestroy() {
    this.closeVideoPreview();
  },
  created() {
    if (!this.$route.meta.keepAlive) this.bootstrap(true);
  },
  methods: {
    getPostContentAnchor(target) {
      if (!(target instanceof Element)) return null;
      let node = target;
      while (node) {
        if (node instanceof HTMLAnchorElement && node.getAttribute('href')) return node;
        if (node.classList.contains('post_detail__content')) return null;
        node = node.parentElement;
      }
      return null;
    },
    onPostContentClick(event) {
      const anchor = this.getPostContentAnchor(event.target);
      if (!anchor) return;
      const routeTarget = resolveForumContentLink(anchor.getAttribute('href') || '', window.location.href);
      if (!routeTarget) return;
      event.preventDefault();
      const navigation = this.$router.push(routeTarget);
      if (navigation && typeof navigation.catch === 'function') {
        navigation.catch(() => {});
      }
    },
    getForumLoginRefreshConfig() {
      return {
        routeName: 'forum-post-detail',
        when: () => Boolean(this.postId),
        onRefresh: () => this.reloadAfterLogin(),
      };
    },
    async reloadAfterLogin() {
      if (!this.postId) return;
      await this.loadCurrentUid();
      const detailOk = await this.fetchDetail();
      if (!detailOk) return;
      this.page = 1;
      this.commentFinished = false;
      this.comments = [];
      await this.fetchComments(true);
    },
    getCachedDetail() {
      return readForumPostSnapshot(this.postId);
    },
    getDetailTrackingFields() {
      return {
        page_type: '帖子详情页',
        post_id: this.postId,
        post_title: this.detail.title || '',
        group_id: this.detail.groupId || '',
        group_name: this.detail.groupName || '',
      };
    },
    trackPostOpt(buttonType) {
      this.EVENT_TRACKING(EVENT_TYPE_ENUM.platform_group_home_post_opt_click, {
        ...this.getDetailTrackingFields(),
        button_type: buttonType,
      });
    },
    savePostInteractionSnapshot() {
      saveForumPostInteractionFromPost(this.detail, this.postId);
    },
    async bootstrap(reset = false) {
      if (!this.postId) return;
      const postChanged = this.bootstrappedPostId !== this.postId;
      if (postChanged) this.resetPageState();
      this.bootstrappedPostId = this.postId;
      const shouldFocusComment = this.$route.query.focusComment === '1';
      if (!shouldFocusComment) {
        this.closeCommentBox();
        this.scrollToDetailTop();
      }
      this.loadCurrentUid();
      const detailOk = await this.fetchDetail();
      if (!detailOk) return;
      const commentListPromise = reset || postChanged || !this.comments.length ? this.fetchComments(true) : Promise.resolve();
      if (shouldFocusComment) {
        this.scrollToCommentModule();
        this.openCommentBox();
      }
      await commentListPromise;
      if (shouldFocusComment) this.scrollToCommentModule();
      else this.scrollToDetailTop();
    },
    resetPageState() {
      this.detail = {};
      this.comments = [];
      this.commentNum = 0;
      this.page = 1;
      this.commentFinished = false;
      this.commentLoading = false;
      this.commentRequesting = false;
      this.commentError = false;
      this.activeTab = 'all';
      this.sortType = 0;
      this.showCommentBox = false;
      this.commentContent = '';
      this.commentImg = {};
      this.replyTarget = null;
      this.closeVideoPreview();
      this.mediaLoadError = {};
    },
    normalizeDetail(data) {
      const detail = data.detail || data.postDetail || data.postsDetail || data;
      if (!detail) return null;
      const postId = detail.postsId || detail.postId;
      if (!postId) return null;
      return { ...detail, postsId: postId };
    },
    async fetchDetail() {
      const cachedDetail = this.getCachedDetail();
      const requestSeq = ++this.detailRequestSeq;
      this.detailLoading = !this.hasDetail;
      this.detailError = false;
      this.detailErrorText = '帖子加载失败，请稍后重试';
      try {
        const res = await getPostDetail(this.postId);
        if (requestSeq !== this.detailRequestSeq) return;
        const nextDetail = this.normalizeDetail((res && res.data) || {});
        if (!nextDetail) {
          this.detail = cachedDetail ? { ...cachedDetail, postsId: cachedDetail.postsId || cachedDetail.postId || this.postId } : {};
          this.detailError = !cachedDetail;
          this.detailErrorText = '该帖子已被删除';
          return Boolean(cachedDetail);
        }
        this.detail = nextDetail;
        this.commentNum = Number(nextDetail.commentNum) || this.commentNum;
        this.savePostInteractionSnapshot();
        this.EVENT_TRACKING(EVENT_TYPE_ENUM.platform_group_square_page_show, {
          ...this.getDetailTrackingFields(),
        });
        return true;
      } catch (error) {
        if (cachedDetail) {
          this.detail = { ...cachedDetail, postsId: cachedDetail.postsId || cachedDetail.postId || this.postId };
          this.commentNum = Number(this.detail.commentNum) || this.commentNum;
          Toast('网络异常，已展示本地缓存内容');
          return true;
        } else {
          this.detail = {};
          this.detailError = true;
          this.detailErrorText = '帖子加载失败，请稍后重试';
          this.comments = [];
          this.commentFinished = true;
          this.commentLoading = false;
          return false;
        }
      } finally {
        if (requestSeq === this.detailRequestSeq) this.detailLoading = false;
      }
    },
    getCommentId(item = {}) {
      return item.id || item.commentId || '';
    },
    getReplyCount(item = {}) {
      return Math.max(item.replayNum || 0, item.replyNum || 0, (item.replyList && item.replyList.length) || 0);
    },
    setCommentPraiseState(item, nextPraise, nextPraiseNum) {
      this.$set(item, 'praise', nextPraise);
      this.$set(item, 'praiseNum', nextPraiseNum);
    },
    patchCommentPraiseState(target, nextPraise) {
      const commentId = String(this.getCommentId(target));
      const nextPraiseNum = Math.max(0, Number((target && target.praiseNum) || 0) + (nextPraise ? 1 : -1));
      this.setCommentPraiseState(target, nextPraise, nextPraiseNum);
      if (!commentId) return;

      const patchList = (list = []) => {
        list.forEach((item) => {
          if (String(this.getCommentId(item)) === commentId) {
            this.setCommentPraiseState(item, nextPraise, nextPraiseNum);
          }
          if (item.replyList && item.replyList.length) {
            patchList(item.replyList);
          }
        });
      };

      patchList(this.comments);
    },
    isTopComment(item) {
      const parentId = item.parentCommentId;
      return parentId === undefined || parentId === null || String(parentId) === '0';
    },
    addReplyToMap(map, parentId, reply) {
      const key = parentId === undefined || parentId === null ? '' : String(parentId);
      if (!key || key === '0') return;
      if (!map[key]) this.$set(map, key, []);
      if (!map[key].some((item) => String(this.getCommentId(item)) === String(this.getCommentId(reply)))) map[key].push(reply);
    },
    normalizeComments(list = []) {
      const records = list.map((item) => ({ ...item, commentId: item.commentId || item.id || '' }));
      const replyMap = {};
      records.forEach((item) => {
        if (this.isTopComment(item)) return;
        this.addReplyToMap(replyMap, item.parentCommentId, item);
        this.addReplyToMap(replyMap, item.commentId, item);
      });
      let topList = records.filter(this.isTopComment).map((item, index) => ({
        ...item,
        floor: item.floor || (this.page - 1) * PAGE_SIZE + index + 1,
        replyList: replyMap[String(this.getCommentId(item))] || item.replyList || [],
      }));
      if (this.activeTab === 'owner' && this.detail.uid !== undefined) topList = topList.filter((item) => String(item.uid || '') === String(this.detail.uid));
      if (this.sortType === 1) topList = topList.slice().sort((a, b) => (b.praiseNum || 0) - (a.praiseNum || 0));
      return { records, topList };
    },
    async fetchComments(reset = false) {
      if (this.commentRequesting) return;
      if (this.detailError || !this.hasDetail) {
        if (reset) {
          this.page = 1;
          this.comments = [];
        }
        this.commentFinished = true;
        this.commentLoading = false;
        return;
      }
      const requestPage = reset ? 1 : this.page;
      if (reset) {
        this.page = 1;
        this.comments = [];
        this.commentFinished = false;
      }
      if (this.commentFinished) {
        this.commentLoading = false;
        return;
      }
      this.commentRequesting = true;
      this.commentLoading = true;
      this.commentError = false;
      try {
        const res = await getCommentList({ postId: this.postId, current: requestPage, size: PAGE_SIZE });
        const data = (res && res.data) || {};
        const list = data.commentList || data.records || data.list || [];
        const normalized = this.normalizeComments(list);
        if (reset) this.comments = normalized.topList;
        else this.comments.push(...normalized.topList);
        this.commentFinished = normalized.records.length < PAGE_SIZE;
        this.page = requestPage + 1;
        if (typeof data.commentNum === 'number') {
          this.commentNum = data.commentNum;
          this.$set(this.detail, 'commentNum', data.commentNum);
          this.savePostInteractionSnapshot();
        }
      } catch (error) {
        this.commentError = true;
        this.commentFinished = true;
      } finally {
        this.commentRequesting = false;
        this.commentLoading = false;
      }
    },
    onTabChange(tab) {
      if (this.activeTab === tab) return;
      this.activeTab = tab;
      this.fetchComments(true);
    },
    onToggleSort() {
      this.sortType = this.sortType === 0 ? 1 : 0;
      this.fetchComments(true);
    },
    mediaImageUrl(media) {
      return media.picUrl || media.picBlurryUrl || '';
    },
    getForumMediaBackgroundStyle,
    getForumMediaCoverUrl,
    formatMediaDuration(duration) {
      const sec = Math.max(0, Math.floor(Number(duration) || 0));
      const mm = Math.floor(sec / 60);
      const ss = sec % 60;
      return `${mm < 10 ? `0${mm}` : mm}:${ss < 10 ? `0${ss}` : ss}`;
    },
    getMediaKey(media, index) {
      return String(media.detailId || media.id || media.picUrl || media.videoUrl || index);
    },
    previewPostImage(media) {
      const url = this.mediaImageUrl(media);
      if (!url || !this.postImageUrls.length) return;
      const startPosition = Math.max(this.postImageUrls.indexOf(url), 0);
      ImagePreview({ images: this.postImageUrls, startPosition });
    },
    markMediaError(media, index) {
      this.$set(this.mediaLoadError, this.getMediaKey(media, index), true);
    },
    openVideoPreview(media) {
      if (!media || !media.videoUrl) return;
      this.previewVideo = media;
      this.$nextTick(() => {
        const player = this.$refs.previewVideoPlayer;
        if (!player) return;
        const play = player.play;
        if (typeof play !== 'function') return;
        try {
          const playTask = play.call(player);
          if (playTask && typeof playTask.catch === 'function') playTask.catch(() => {});
        } catch (error) {
          // ignore autoplay blocks
        }
      });
    },
    closeVideoPreview() {
      const player = this.$refs.previewVideoPlayer;
      if (player) {
        player.pause();
        try {
          player.currentTime = 0;
        } catch (error) {
          // ignore unsupported seek states
        }
      }
      this.previewVideo = null;
    },
    onVideoPreviewError() {
      Toast('视频加载失败，请稍后重试');
    },
    async loadCurrentUid() {
      const res = await getUserCenter();
      if (res && res.data && res.data.uid != null) {
        this.currentUid = res.data.uid;
      }
    },
    onPostMoreSelect(action) {
      this.showPostMoreSheet = false;
      if (action.key === 'delete') this.onDeletePost();
    },
    async onDeletePost() {
      try {
        await Dialog.confirm({
          title: '提示',
          message: '确定删除该帖子？删除后不可恢复',
        });
        await deleteOwnPost(this.postId);
        Toast.success('已删除');
        markForumPostDeleted(this.detail.groupId, this.postId);
        this.$router.back();
      } catch (error) {
        // 用户取消或删除失败
      }
    },
    goGroup() {
      if (!this.detail.groupId) return;
      this.$router.push({ name: 'forum-group', params: { groupId: this.detail.groupId } });
    },
    openCommentBox(target) {
      if (!this.hasDetail) {
        Toast('帖子状态异常，请刷新后重试');
        return;
      }
      this.trackPostOpt('评论');
      this.replyTarget = target || null;
      this.showCommentBox = true;
      this.$nextTick(() => {
        if (this.$refs.commentInput) this.$refs.commentInput.focus();
      });
    },
    closeCommentBox() {
      this.showCommentBox = false;
      this.replyTarget = null;
    },
    scrollToCommentModule() {
      this.$nextTick(() => {
        const target = this.$refs.commentTabs;
        if (!target) return;
        window.requestAnimationFrame(() => {
          const targetTop = target.getBoundingClientRect().top + window.pageYOffset;
          window.scrollTo(0, targetTop > 0 ? targetTop : 0);
        });
      });
    },
    scrollToDetailTop() {
      this.$nextTick(() => {
        window.requestAnimationFrame(() => {
          window.scrollTo(0, 0);
        });
      });
    },
    async onPraisePost() {
      if (!this.hasDetail) return;
      this.trackPostOpt(this.detail.praise ? '取消点赞' : '点赞');
      const res = await praiseOrCancelPosts(this.detail.postsId, this.detail.praise ? 2 : 1).catch((err) => err);
      if (!toastInteraction(res, { successMsg: this.detail.praise ? '已取消点赞' : '已点赞' })) return;
      this.$set(this.detail, 'praise', !this.detail.praise);
      this.$set(this.detail, 'praiseNum', (this.detail.praiseNum || 0) + (this.detail.praise ? 1 : -1));
      this.savePostInteractionSnapshot();
    },
    async onCollectPost() {
      if (!this.hasDetail) return;
      this.trackPostOpt(this.detail.collect ? '取消收藏' : '收藏');
      const res = await collectOrCancelPosts(this.detail.postsId, this.detail.collect ? 2 : 1).catch((err) => err);
      if (!isInteractionOk(res)) return toastInteraction(res, { fallbackMsg: '操作失败' });
      this.$set(this.detail, 'collect', !this.detail.collect);
      this.$set(this.detail, 'collectNum', (this.detail.collectNum || 0) + (this.detail.collect ? 1 : -1));
      this.savePostInteractionSnapshot();
    },
    onReply(item) {
      const commentId = this.getCommentId(item);
      if (!commentId) return;
      this.openCommentBox({ commentId, parentCommentId: commentId, nickname: item.nickname });
    },
    onReplySub(payload) {
      const parent = payload && payload.parent;
      const sub = payload && payload.sub;
      const parentId = this.getCommentId(parent || {});
      const subId = this.getCommentId(sub || {});
      if (!parentId || !subId) return;
      // 回复子评论：commentId 传一级评论 id，parentCommentId 传被回复子评论 id
      this.openCommentBox({ commentId: parentId, parentCommentId: subId, replyUid: sub.uid, nickname: sub.nickname });
    },
    async onPraiseComment(item) {
      const id = this.getCommentId(item);
      if (!id) return;
      const key = String(id);
      if (pendingCommentPraiseIds.has(key)) return;
      pendingCommentPraiseIds.add(key);
      const nextPraise = !item.praise;
      try {
        this.EVENT_TRACKING(EVENT_TYPE_ENUM.platform_group_post_detail_praise_comment, {
          ...this.getDetailTrackingFields(),
          button_type: nextPraise ? '点赞' : '取消点赞',
          comment_content: item.content || '',
        });
        const res = await praiseOrCancelComments(id, nextPraise ? 1 : 2).catch((err) => err);
        if (!toastInteraction(res, { successMsg: nextPraise ? '已点赞' : '已取消点赞' })) return;
        this.patchCommentPraiseState(item, nextPraise);
      } finally {
        pendingCommentPraiseIds.delete(key);
      }
    },
    pickCommentImage() {
      if (this.commentImg.uploading) {
        Toast('图片还在上传中，请稍后');
        return;
      }
      this.$refs.commentImageInput.click();
    },
    async onCommentImagePicked(event) {
      const file = event.target.files && event.target.files[0];
      event.target.value = '';
      if (!file) return;
      if (file.size > 10 * 1024 * 1024) {
        Toast('图片大小不得超过10M，请重新上传');
        return;
      }
      const preview = URL.createObjectURL(file);
      this.commentImg = { preview, uploading: true, failed: false };
      try {
        const formData = new FormData();
        formData.append('file', file, file.name);
        const res = await uploadForumFile(formData);
        this.commentImg = { preview, url: res && res.data, uploading: false, failed: false };
      } catch (error) {
        this.commentImg = { preview, uploading: false, failed: true };
      }
    },
    removeCommentImage() {
      if (this.commentImg.preview) URL.revokeObjectURL(this.commentImg.preview);
      this.commentImg = {};
    },
    async submitComment() {
      const content = this.commentContent.trim();
      const img = this.commentImg.url;
      if (!content && !img) {
        Toast('评论内容不能为空');
        return;
      }
      if (this.commentImg.uploading) {
        Toast('图片还在上传中，请稍后');
        return;
      }
      this.commentSubmitting = true;
      try {
        const payload = {
          postId: this.postId,
          content,
          imgList: img ? [img] : [],
          commentId: (this.replyTarget && this.replyTarget.commentId) || 0,
          parentCommentId: (this.replyTarget && this.replyTarget.parentCommentId) || 0,
        };
        // 仅回复子评论时传 replyUid；一级评论或回复一级评论不传
        if (this.replyTarget && this.replyTarget.replyUid) {
          payload.replyUid = this.replyTarget.replyUid;
        }
        const res = await comment(payload);
        const experience = res && res.data && res.data.experience;
        this.EVENT_TRACKING(EVENT_TYPE_ENUM.platform_group_post_detail_praise_comment, {
          ...this.getDetailTrackingFields(),
          button_type: '评论',
          comment_content: content,
        });
        Toast.success(`评论成功${experience ? `，经验 +${experience}` : ''}`);
        this.commentContent = '';
        this.replyTarget = null;
        this.removeCommentImage();
        this.showCommentBox = false;
        this.commentNum += 1;
        this.$set(this.detail, 'commentNum', Number(this.detail.commentNum || 0) + 1);
        await this.fetchComments(true);
        this.savePostInteractionSnapshot();
      } catch (error) {
        // 接口失败提示由 axios 拦截器统一处理
      } finally {
        this.commentSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
.post_detail {
  min-height: 100vh;
  padding-bottom: 56px;
  background: #f5f5f5;
}

.post_detail__status {
  padding: 120px 24px;
  color: #999;
  font-size: 14px;
  text-align: center;
}

.post_detail__status--error p {
  margin: 0 0 16px;
}

.post_detail__status--error button,
.post_detail__comment_error button {
  border: 0;
  color: #fd383c;
  background: transparent;
}

.post_detail__card {
  position: relative;
  width: 100%;
  box-sizing: border-box;
  padding: 12px 20px;
  background: #fff;
}

.post_detail__audit {
  margin-bottom: 10px;
  border-radius: 6px;
  padding: 8px 10px;
  color: #a15c00;
  background: #fff7e6;
  font-size: 12px;
}

.post_detail__top_action {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 8px;
}

.post_detail__top_action button {
  border: 0;
  color: #6879ad;
  background: transparent;
  font-size: 13px;
}

.post_detail__user {
  display: flex;
  align-items: center;
}

.post_detail__avatar_wrap {
  position: relative;
  width: 42px;
  height: 42px;
  margin-right: 10px;
  flex-shrink: 0;
}

.post_detail__avatar {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background-color: #e5e5e5;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.post_detail__frame {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 64px;
  height: 64px;
  pointer-events: none;
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  transform: translate(-50%, -50%);
}

.post_detail__user_info {
  min-width: 0;
  flex: 1;
}

.post_detail__name_row {
  display: flex;
  align-items: center;
  width: 100%;
}

.post_detail__name_main {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
}

.post_detail__more_btn {
  flex-shrink: 0;
  margin-left: 8px;
  padding: 0 4px;
  border: 0;
  background: transparent;
  color: #666;
  font-size: 18px;
  font-weight: 700;
  line-height: 32px;
}

.post_detail__name {
  max-width: 200px;
  color: #333;
  font-size: 14px;
  font-weight: 700;
  line-height: 16px;
}

.post_detail__time {
  margin: 6px 0 0;
  color: #999;
  font-size: 12px;
  line-height: 15px;
}

.post_detail__title {
  width: 100%;
  margin: 12px 0 0;
  color: #333;
  font-size: 17px;
  font-weight: 700;
  line-height: 22px;
  text-align: left;
  word-break: break-word;
}

.post_detail__tag {
  position: relative;
  top: -1px;
  display: inline-block;
  height: 16px;
  margin-right: 4px;
  border-radius: 3px;
  padding: 0 6px;
  color: #fbfbfb;
  background: #d09962;
  font-size: 10px;
  line-height: 16px;
  vertical-align: middle;
}

.post_detail__tag--top {
  background: #ff7a00;
}

.post_detail__tag--essence {
  background: #00d0c1;
}

.post_detail__title_text {
  vertical-align: middle;
  /* span 被 reset 设为 normal，需单独加粗标题文案 */
  font-weight: bold !important;
}

.post_detail__content {
  margin-top: 14px;
  color: #666;
  font-size: 14px;
  line-height: 20px;
  text-align: left;
  white-space: pre-wrap;
  overflow-wrap: break-word;
  word-break: break-word;
}

.post_detail__content ::v-deep img {
  max-width: 100%;
  height: auto;
  vertical-align: middle;
}

.post_detail__content ::v-deep a {
  color: #0249ff;
  text-decoration: underline;
}

.post_detail__content ::v-deep p {
  margin: 0 0 8px;
}

.post_detail__content ::v-deep p:last-child {
  margin-bottom: 0;
}

/* 后台富文本多为 h1+span+strong，reset 的 font 简写会冲掉 strong 加粗 */
.post_detail__content ::v-deep h1,
.post_detail__content ::v-deep h2,
.post_detail__content ::v-deep h3,
.post_detail__content ::v-deep h4,
.post_detail__content ::v-deep h5,
.post_detail__content ::v-deep h6 {
  margin: 0 0 8px;
  padding: 0;
  border: 0;
  font-size: inherit;
  font-weight: normal;
  line-height: inherit;
}

.post_detail__content ::v-deep strong,
.post_detail__content ::v-deep b {
  font-weight: bold !important;
}

.post_detail__img_list {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 12px;
}

.post_detail__img_item {
  width: calc((100% - 8px) / 2);
  height: 110px;
  flex: 0 0 calc((100% - 8px) / 2);
  margin-bottom: 8px;
  border-radius: 8px;
  overflow: hidden;
  background: #f0f0f0;
}

.post_detail__img_item + .post_detail__img_item {
  margin-left: 8px;
}

.post_detail__img_item:nth-child(2n + 1) {
  margin-left: 0;
}

.post_detail__media {
  position: relative;
  display: block;
  width: 100%;
  height: 100%;
  border: 0;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  object-fit: cover;
}

.post_detail__media_probe {
  display: none;
}

.post_detail__media_error {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  border: 0;
  color: #999;
  background: #f3f4f6;
  font-size: 12px;
}

.post_detail__video_card {
  position: relative;
  padding: 0;
  overflow: hidden;
  background-color: #111;
}

.post_detail__video_probe {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
}

.post_detail__video_shade {
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.18);
  pointer-events: none;
}

.post_detail__video_play {
  position: absolute;
  left: 50%;
  top: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  margin-left: -19px;
  margin-top: -19px;
  color: #fff;
  background-color: rgba(0, 0, 0, 0.56);
  border-radius: 50%;
  pointer-events: none;
}

.post_detail__video_play svg {
  width: 22px;
  height: 22px;
  margin-left: 2px;
}

.post_detail__video_duration {
  position: absolute;
  right: 6px;
  bottom: 5px;
  padding: 1px 5px;
  color: #fff;
  font-size: 10px;
  line-height: 14px;
  background-color: rgba(0, 0, 0, 0.6);
  border-radius: 7px;
  pointer-events: none;
}

.video_preview {
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 52px 0 48px;
  background-color: #000;
  box-sizing: border-box;
}

.video_preview__close {
  position: fixed;
  top: 14px;
  right: 14px;
  z-index: 3001;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  color: #fff;
  font-size: 28px;
  line-height: 34px;
  text-align: center;
  background-color: rgba(0, 0, 0, 0.45);
}

.video_preview__player {
  display: block;
  width: 100%;
  max-height: 82vh;
  background-color: #000;
}

.video_preview__hint {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 18px;
  color: rgba(255, 255, 255, 0.72);
  font-size: 12px;
  line-height: 16px;
  text-align: center;
  pointer-events: none;
}

.post_detail__com_top {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 16px;
  margin-bottom: 4px;
}

.post_detail__com_tabs {
  min-width: 0;
  flex: 1;
}

.post_detail__com_tab {
  position: relative;
  border: 0;
  padding: 0 8px 7px;
  color: #666;
  background: transparent;
  font-size: 13px;
}

.post_detail__com_tab.active {
  color: #333;
  font-weight: 600;
}

.post_detail__com_tab.active::after {
  position: absolute;
  left: 50%;
  bottom: 0;
  width: 28px;
  height: 3px;
  border-radius: 2px;
  background: #fd383c;
  content: '';
  transform: translateX(-50%);
}

.post_detail__com_count {
  position: absolute;
  right: -20px;
  top: -4px;
  min-width: 18px;
  color: #999;
  font-size: 10px;
  font-weight: 400;
  line-height: 1;
  text-align: center;
  white-space: nowrap;
}

.post_detail__com_sort {
  flex-shrink: 0;
  border: 0;
  color: #666;
  background: transparent;
  font-size: 14px;
}

.post_detail__comment_error {
  padding: 48px 16px;
  color: #999;
  font-size: 13px;
  text-align: center;
}

.post_detail__bottom {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-top: 1px solid #f0f0f0;
  background: #fff;
}

.post_detail__bottom_input {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  height: 36px;
  border: 0;
  border-radius: 18px;
  padding: 0 12px;
  color: #999;
  background: #f5f5f5;
  font-size: 13px;
}

.post_detail__bottom_input span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.post_detail__bottom_input_icon {
  width: 16px;
  height: 16px;
  margin-right: 6px;
  flex-shrink: 0;
}

.post_detail__bottom_btn {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 34px;
  border: 0;
  margin-left: 10px;
  padding: 0;
  background: transparent;
}

.post_detail__bottom_icon {
  width: 18px;
  height: 18px;
  margin-right: 3px;
  flex-shrink: 0;
}

.post_detail__bottom_count {
  color: #c2c2c2;
  font-size: 12px;
}

.post_detail__bottom_count--active {
  color: #ff7352;
}

.comment_box {
  box-sizing: border-box;
  padding: 20px 16px 8px;
  background: #fff;
}

.comment_box textarea {
  width: 100%;
  min-height: 88px;
  box-sizing: border-box;
  border: 0;
  border-radius: 10px;
  outline: none;
  padding: 10px;
  color: #333;
  background: #f5f5f5;
  font-size: 14px;
  line-height: 20px;
  resize: none;
}

.comment_box__preview {
  position: relative;
  width: 60px;
  height: 60px;
  margin-top: 8px;
}

.comment_box__preview img {
  width: 100%;
  height: 100%;
  border-radius: 4px;
  object-fit: cover;
}

.comment_box__preview button {
  position: absolute;
  right: -6px;
  top: -6px;
  width: 18px;
  height: 18px;
  border: 0;
  border-radius: 50%;
  color: #fff;
  background: rgba(0, 0, 0, 0.5);
}

.comment_box__preview span {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  color: #fff;
  background: rgba(0, 0, 0, 0.45);
  font-size: 10px;
  line-height: 18px;
  text-align: center;
}

.comment_box__op {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
}

.comment_box__upload {
  width: 60px;
  height: 32px;
  border: 0;
  border-radius: 16px;
  color: #666;
  background: #f5f5f5;
  font-size: 13px;
}

.comment_box__send {
  height: 32px;
  border: 0;
  border-radius: 16px;
  padding: 0 18px;
  color: #fff;
  background: #ff7a00;
  font-size: 14px;
  line-height: 1;
}

.comment_box__send:disabled {
  background: #d1d5db;
}

.comment_box__file {
  display: none;
}
</style>
